import { buildPageJsonLd, serializeJsonLd } from "./jsonld";
import type { ResolvedSeoRoute } from "./resolveSeoRoute";
import { getPageSeo, indexedPathFor } from "./seoContent";
import type { SeoLocale } from "./seoTypes";
import type { SeoFaqItem, SeoPlan, SeoTool } from "./sources";

/**
 * Everything that goes into `<head>` for one URL, as plain data. `SeoHead`
 * turns it into Helmet children in the browser; the prerender serializes it
 * into static HTML. One builder, so the two never disagree.
 */

export const SEO_LOCALES: readonly SeoLocale[] = ["cs", "en"];

export const OG_LOCALE: Record<SeoLocale, string> = {
  cs: "cs_CZ",
  en: "en_US",
};

export const OG_IMAGE_WIDTH = 1200;
export const OG_IMAGE_HEIGHT = 630;

export function ogImagePath(lang: SeoLocale): string {
  return `/og/zettl-${lang}.png`;
}

export interface HeadTag {
  tag: "meta" | "link" | "script";
  attrs: Record<string, string>;
  /** Text content (only for `script`). */
  content?: string;
}

export interface HeadModel {
  lang: SeoLocale;
  title: string;
  tags: HeadTag[];
}

export interface HeadOptions {
  origin: string;
  /** False on previews and local builds: every page is `noindex` there. */
  deploymentIndexable: boolean;
  plans?: readonly SeoPlan[];
  faq?: readonly SeoFaqItem[];
  /** A free tool page's WebApplication and HowTo. */
  tool?: SeoTool | null;
  /** The day the content last changed (YYYY-MM-DD): the WebPage's dateModified. */
  updated?: string | null;
  /** Sources the page lists as checked: the WebPage's citation. */
  citations?: readonly { name: string; url: string }[];
  sameAs?: readonly string[];
}

/** hreflang alternates for an indexed page, plus x-default. Empty for everything else. */
export function buildAlternates(
  route: Pick<ResolvedSeoRoute, "pageKey">,
  origin: string
): { hreflang: string; href: string }[] {
  const alternates: { hreflang: string; href: string }[] = [];
  for (const loc of SEO_LOCALES) {
    const path = indexedPathFor(route.pageKey, loc);
    if (path) alternates.push({ hreflang: loc, href: `${origin}${path}` });
  }
  if (alternates.length === 0) return alternates;
  // Czech is the primary market, so the Czech page is x-default everywhere.
  // Not the root: it only 307-redirects, and alternates should be pages.
  const xDefault = `${origin}${indexedPathFor(route.pageKey, "cs")}`;
  alternates.push({ hreflang: "x-default", href: xDefault });
  return alternates;
}

export function buildHead(route: ResolvedSeoRoute, options: HeadOptions): HeadModel {
  const { lang, pageKey, canonicalPath } = route;
  const { origin } = options;
  const copy = getPageSeo(pageKey, lang);
  const indexable = route.indexable && options.deploymentIndexable;
  const canonical = `${origin}${indexedPathFor(pageKey, lang) ?? canonicalPath}`;
  const image = `${origin}${ogImagePath(lang)}`;

  const meta = (attrs: Record<string, string>): HeadTag => ({ tag: "meta", attrs });
  const tags: HeadTag[] = [
    meta({ name: "description", content: copy.description }),
    meta({ name: "robots", content: indexable ? "index,follow" : "noindex,nofollow" }),
    { tag: "link", attrs: { rel: "canonical", href: canonical } },
    meta({ property: "og:site_name", content: "Zettl" }),
    meta({ property: "og:title", content: copy.title }),
    meta({ property: "og:description", content: copy.description }),
    meta({ property: "og:type", content: "website" }),
    meta({ property: "og:url", content: canonical }),
    meta({ property: "og:image", content: image }),
    meta({ property: "og:image:width", content: String(OG_IMAGE_WIDTH) }),
    meta({ property: "og:image:height", content: String(OG_IMAGE_HEIGHT) }),
    meta({ property: "og:image:alt", content: copy.title }),
    meta({ property: "og:locale", content: OG_LOCALE[lang] }),
    meta({ name: "twitter:card", content: "summary_large_image" }),
    meta({ name: "twitter:title", content: copy.title }),
    meta({ name: "twitter:description", content: copy.description }),
    meta({ name: "twitter:image", content: image }),
  ];

  const alternates = route.indexable ? buildAlternates(route, origin) : [];
  for (const alt of alternates) {
    tags.push({ tag: "link", attrs: { rel: "alternate", hreflang: alt.hreflang, href: alt.href } });
  }

  if (route.indexable) {
    const jsonLd = buildPageJsonLd({
      origin,
      lang,
      url: canonical,
      title: copy.title,
      description: copy.description,
      isLanding: pageKey === "landing",
      plans: options.plans,
      faq: options.faq,
      tool: options.tool,
      dateModified: options.updated,
      citations: options.citations,
      sameAs: options.sameAs,
    });
    tags.push({
      tag: "script",
      attrs: { type: "application/ld+json" },
      content: serializeJsonLd(jsonLd),
    });
  }

  return { lang, title: copy.title, tags };
}

export function escapeHtml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

/**
 * Static HTML for the head model. Every tag carries `data-rh="true"`, so
 * react-helmet-async adopts and replaces them on the client instead of
 * duplicating them.
 */
export function renderHeadHtml(model: HeadModel): string {
  const lines = [`<title data-rh="true">${escapeHtml(model.title)}</title>`];
  for (const { tag, attrs, content } of model.tags) {
    const attrString = Object.entries(attrs)
      .map(([k, v]) => `${k}="${escapeHtml(v)}"`)
      .join(" ");
    if (tag === "script") {
      // JSON-LD is already `<`-escaped by serializeJsonLd.
      lines.push(`<script data-rh="true" ${attrString}>${content ?? ""}</script>`);
    } else {
      lines.push(`<${tag} data-rh="true" ${attrString} />`);
    }
  }
  return lines.join("\n    ");
}
